import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
} from '@angular/core';
import { SizeableDirective } from '../../directives/sizeable.directive';

@Component({
  selector: 'ask-button-v2',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './button-v2.component.html',
  styleUrl: './button-v2.component.scss',
  host:{
    '[class.-wide]': 'wide',
  },
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ButtonV2Component implements AfterViewInit, OnDestroy {
  private readonly _defaultSize = 16;
  protected _disabled = false;
  protected iconOnlyButton = false;
  private mutationObserver?: MutationObserver;

  @ContentChildren(SizeableDirective)
  private sizeableComps?: QueryList<SizeableDirective>;
  @ViewChild('contentWrapper', { read: ElementRef })
  private contentWrapper!: ElementRef<HTMLButtonElement>;


  // Variations as input properties with default values
  @Input() size: 'smallest' | 'small' | 'medium' | 'large' | 'larger' | 'pill' =
    'medium';
  @Input() shape: 'rounded' | 'square' = 'square';
  @Input() appearance:
    | 'brand'
    | 'opaque'
    | 'opaque-inverse'
    | 'transparent'
    | 'transparent-inverse'
    | 'outline'
    | 'outline-inverse'
    | 'notification'
    | 'sofia-button' = 'opaque';

  @Input() set disabled(value: boolean) {
    this._disabled = value;
    this.updateChilds();
  }
  @Input() selected: boolean = false;
  @Input() wide: boolean = false;
  @Input() styles: { [style: string]: any; } = {};

  // Event handlers
  @Output() onClick = new EventEmitter<MouseEvent>();
  @Output() onFocus = new EventEmitter<FocusEvent>();
  @Output() onBlur = new EventEmitter<FocusEvent>();

  // Compute CSS classes based on inputs
  get buttonClasses(): string[] {
    let classes = [`-${this.appearance}`, `-${this.shape}`, `-${this.size}`];

    if (this.selected) {
      classes = [...classes, 'selected'];
    }

    if (this.wide) {
      classes = [...classes, '-wide'];
    }

    if (this.iconOnlyButton) {
      classes = [...classes, '-icon-only'];
    }

    if (this.disabled) {
      classes = [...classes, '-disabled']
    }

    return classes;
  }

  constructor(private cd: ChangeDetectorRef){}

  ngAfterContentInit() {
    setTimeout(() => {
      this.setIconOnly();
      this.updateChilds();
    });
  }

  ngAfterViewInit() {
    this.mutationObserver = new MutationObserver(() => {
      this.setIconOnly();
    });
    this.mutationObserver.observe(this.contentWrapper.nativeElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  ngOnDestroy() {
    this.mutationObserver?.disconnect();
  }

  private setIconOnly(){
    const childNodes = Array.from(
      this.contentWrapper.nativeElement.childNodes
    ).filter((node) => {
      if(node.nodeType === Node.COMMENT_NODE) return false;
      if (node.nodeType === Node.TEXT_NODE)
        return node.textContent && node.textContent.trim().length > 0;
      return true;
    });

    const isSingleIcon =
      childNodes.length === 1 && this.sizeableComps?.length === 1;

    this.iconOnlyButton = isSingleIcon;
    this.cd.detectChanges();
  }

  private updateChilds() {
    if (!this.sizeableComps) return;
    this.sizeableComps!.forEach((child) => {
      child.disabled = this._disabled;
      child.ngOnChanges();
      this.cd.detectChanges();
    });
    this.cd.detectChanges();
  }
}
